/**
 * Formatting utilities for numbers, dates, and other data
 */

/**
 * Formats a number with thousand separators (commas)
 * @param {number} num - Number to format
 * @returns {string} Formatted number (e.g., "1,234")
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) {
    return "0";
  }
  return Math.round(num).toLocaleString("en-US");
};

/**
 * Formats a number as a percentage with specified decimal places
 * @param {number} num - Number to format (e.g., 94.6343)
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted percentage (e.g., "94.63%")
 */
export const formatPercentage = (num, decimals = 2) => {
  if (num === null || num === undefined || isNaN(num)) {
    return "0.00%";
  }
  return `${num.toFixed(decimals)}%`;
};

/**
 * Formats large numbers with abbreviations (K, M, B)
 * @param {number} num - Number to format
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Abbreviated number (e.g., "4.2M", "1.5K")
 */
export const formatLargeNumber = (num, decimals = 1) => {
  if (num === null || num === undefined || isNaN(num)) {
    return "0";
  }

  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(decimals) + "B";
  }
  if (num >= 1000000) {
    return (num / 1000000).toFixed(decimals) + "M";
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(decimals) + "K";
  }
  return Math.round(num).toString();
};

/**
 * Formats a date to relative time ("2 hours ago", "Yesterday")
 * @param {string|Date} date - Date to format
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date) => {
  if (!date) return "-";

  const now = new Date();
  const then = new Date(date);
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) {
    return "Just now";
  } else if (diffMin < 60) {
    return `${diffMin} minute${diffMin !== 1 ? "s" : ""} ago`;
  } else if (diffHour < 24) {
    return `${diffHour} hour${diffHour !== 1 ? "s" : ""} ago`;
  } else if (diffDay === 1) {
    return `Yesterday at ${then.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })}`;
  } else if (diffDay < 7) {
    return `${diffDay} day${diffDay !== 1 ? "s" : ""} ago`;
  } else {
    return then.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: diffDay > 365 ? "numeric" : undefined,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }
};

/**
 * Formats a decimal number with specified decimal places
 * @param {number} num - Number to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted decimal
 */
export const formatDecimal = (num, decimals = 2) => {
  if (num === null || num === undefined || isNaN(num)) {
    return "0.00";
  }
  return num.toFixed(decimals);
};

/**
 * Gets color class based on success rate
 * @param {number} successRate - Success rate percentage
 * @returns {string} Bootstrap text color class
 */
export const getSuccessRateColorClass = (successRate) => {
  if (successRate >= 95) return "text-success";
  if (successRate >= 85) return "text-warning";
  return "text-danger";
};
